import type { Dispatch, SetStateAction } from "react"

import { useTranslation } from "react-i18next"

import type { FilthBackendFormState } from "./filth-backends-form-utils"
import type { FilthBackend } from "@/api/filth-backends-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { FilthContextModeFields } from "./filth-context-mode-fields"

type Props = {
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
  editTarget: FilthBackend | null
  form: FilthBackendFormState
  setForm: Dispatch<SetStateAction<FilthBackendFormState>>
  formValid: boolean
  credentialConflict: boolean
  busy: boolean
  handleSubmit: () => Promise<void>
}
export function FilthBackendConnectionDialog({
  dialogOpen,
  setDialogOpen,
  editTarget,
  form,
  setForm,
  formValid,
  credentialConflict,
  busy,
  handleSubmit,
}: Props) {
  const { t } = useTranslation()
  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="max-h-[90vh] gap-6 overflow-y-auto p-6 sm:max-w-3xl sm:p-8">
        <DialogHeader className="gap-3 pr-6">
          <DialogTitle>
            {editTarget
              ? t("adminDashboard.filthBackends.editTitle", "Edit Prism backend")
              : t("adminDashboard.filthBackends.addTitle", "Add Prism backend")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.filthBackends.dialogDescription",
              "A Prism service that indexes a file source. The bearer token is stored in Key Vault."
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-2">
          <section className="grid gap-6">
            <div className="space-y-2">
              <h4 className="font-display text-base">
                {t("adminDashboard.filthSettings.connectionTitle")}
              </h4>
              <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
                {t("adminDashboard.filthSettings.connectionDescription")}
              </p>
            </div>
            {!editTarget && (
              <div className="grid gap-3">
                <Label htmlFor="filth-id">{t("adminDashboard.filthBackends.field.id", "ID")}</Label>
                <Input
                  className="min-h-10"
                  id="filth-id"
                  placeholder="filth-engineering"
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                />
              </div>
            )}
            <div className="grid gap-3">
              <Label htmlFor="filth-name">
                {t("adminDashboard.filthBackends.field.name", "Name")}
              </Label>
              <Input
                className="min-h-10"
                id="filth-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="filth-url">
                {t("adminDashboard.filthBackends.field.baseUrl", "Base URL")}
              </Label>
              <Input
                className="min-h-10"
                id="filth-url"
                placeholder="https://filth.internal:8000"
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="filth-tenant">
                {t("adminDashboard.filthBackends.field.tenant", "Tenant ID")}
              </Label>
              <Input
                className="min-h-10"
                id="filth-tenant"
                value={form.tenant_id}
                onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
              />
            </div>
            {form.consumer_mode !== "primary" && (
              <div className="grid gap-3">
                <Label htmlFor="filth-scope">
                  {t("adminDashboard.filthBackends.field.scope", "Default scope (optional)")}
                </Label>
                <Input
                  className="min-h-10"
                  id="filth-scope"
                  placeholder="/mnt/share"
                  value={form.default_scope}
                  onChange={(e) => setForm({ ...form, default_scope: e.target.value })}
                />
              </div>
            )}
          </section>
          <section className="grid gap-6 border-t pt-6">
            <div className="space-y-2">
              <h4 className="font-display text-base">
                {t("adminDashboard.filthSettings.accessTitle")}
              </h4>
              <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
                {t("adminDashboard.filthSettings.accessDescription")}
              </p>
            </div>
            <FilthContextModeFields
              companyWide={form.company_wide}
              consumerMode={form.consumer_mode}
              delegatedScope={form.delegated_scope}
              onChange={(patch) =>
                setForm((current) => ({
                  ...current,
                  ...patch,
                  ...(patch.consumer_mode === "primary" ? { default_scope: "" } : {}),
                }))
              }
            />
          </section>
          <section className="grid gap-6 border-t pt-6">
            <div className="space-y-2">
              <h4 className="font-display text-base">
                {t("adminDashboard.filthSettings.credentialsTitle")}
              </h4>
              <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
                {t("adminDashboard.filthSettings.credentialsDescription")}
              </p>
            </div>
            <div className="grid gap-3">
              <Label htmlFor="filth-token">
                {editTarget
                  ? t(
                      "adminDashboard.filthBackends.field.tokenEdit",
                      "Bearer token (leave blank to keep)"
                    )
                  : t("adminDashboard.filthBackends.field.token", "Bearer token (optional)")}
              </Label>
              <Input
                autoComplete="off"
                className="min-h-10"
                disabled={form.credential_secret_name.trim() !== ""}
                id="filth-token"
                type="password"
                value={form.token}
                onChange={(e) => setForm({ ...form, token: e.target.value })}
              />
            </div>
            <div className="grid gap-3">
              <Label htmlFor="filth-credential-secret-name">
                {t(
                  "adminDashboard.filthBackends.field.credentialSecretName",
                  "Pre-provisioned credential name (optional)"
                )}
              </Label>
              <Input
                aria-describedby="filth-credential-secret-name-hint"
                autoComplete="off"
                className="min-h-10 font-mono text-xs"
                disabled={form.token !== ""}
                id="filth-credential-secret-name"
                spellCheck={false}
                value={form.credential_secret_name}
                placeholder={
                  editTarget?.credential_ref?.secret_name || "filth-<id>-<hash>-token-<32 hex>"
                }
                onChange={(e) => setForm({ ...form, credential_secret_name: e.target.value })}
              />
              <p
                id="filth-credential-secret-name-hint"
                className={
                  credentialConflict
                    ? "text-destructive text-sm leading-relaxed"
                    : "text-muted-foreground max-w-prose text-sm leading-relaxed"
                }
              >
                {credentialConflict
                  ? t(
                      "adminDashboard.filthBackends.field.credentialConflict",
                      "Use either a bearer token or a pre-provisioned credential name, not both."
                    )
                  : t(
                      "adminDashboard.filthBackends.field.credentialSecretNameHint",
                      "For servers without Key Vault: the name of a token the operator has already provisioned as HAI_SECRET_<NAME> in the backend environment. Leave blank to keep the current credential."
                    )}
              </p>
            </div>
          </section>
        </div>
        <DialogFooter className="border-t pt-6">
          <Button variant="outline" onClick={() => setDialogOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!formValid || busy} onClick={() => void handleSubmit()}>
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
